import { visit } from "unist-util-visit"
import type { Plugin } from "unified"
import type { Root, Table, TableRow, TableCell, Text } from "mdast"

/**
 * Remark plugin to parse column width hints from table headers.
 *
 * Syntax: | Column Name {width} | Another {width} |
 * Example: | Date {12} | Change {78} | Author {10} |
 *
 * Widths are percentages. The plugin strips the {width} syntax from
 * headers and stores widths as data attributes for rehype to use.
 */
export const remarkTableWidths: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree, "table", (node: Table) => {
      const firstRow = node.children[0] as TableRow | undefined
      if (!firstRow) return

      const widths: (number | null)[] = []
      let hasWidths = false

      // Process each header cell
      for (const cell of firstRow.children as TableCell[]) {
        let width: number | null = null

        // Look for {number} pattern in text nodes
        visit(cell, "text", (textNode: Text) => {
          const match = textNode.value.match(/\s*\{(\d+)\}\s*$/)
          if (match) {
            width = parseInt(match[1], 10)
            hasWidths = true
            // Remove the {width} from the text
            textNode.value = textNode.value.replace(/\s*\{(\d+)\}\s*$/, "").trim()
          }
        })

        widths.push(width)
      }

      // Store widths in the table's data for the rehype plugin to use
      if (hasWidths) {
        node.data = node.data || {}
        node.data.hProperties = node.data.hProperties || {}
        ;(node.data.hProperties as Record<string, unknown>)["data-col-widths"] = widths.join(",")
      }
    })
  }
}
